import { useEffect, useRef } from 'react';
import { ScrollArea } from '../components/ui/scroll-area';
import { User, Headphones, Video, Wifi } from 'lucide-react';

export function LiveTranscript({ transcript, isListening, inputMode, meetingBotStatus }) {
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    // CRITICAL: `block: 'nearest'` prevents scrollIntoView from bubbling up and
    // scrolling the document body / outer page. Without it, every new transcript
    // entry would push the rest of the call screen (metrics, ratings, solution
    // panel) out of view. We also scope the scroll to the ScrollArea's viewport
    // explicitly so even if `nearest` resolves the wrong ancestor, only the
    // transcript itself moves.
    if (!bottomRef.current) return;
    const viewport = scrollRef.current?.querySelector?.('[data-radix-scroll-area-viewport]');
    if (viewport) {
      viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'smooth' });
    } else {
      bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, [transcript]);

  const isMeetingMode = inputMode === 'meeting';

  if (!transcript || transcript.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center p-8" data-testid="transcript-empty">
        {isMeetingMode ? (
          <>
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
              meetingBotStatus === 'in_meeting' || meetingBotStatus === 'in_call' ? 'bg-green-100' :
              meetingBotStatus === 'joining' ? 'bg-yellow-100' : 'bg-zinc-100'
            }`}>
              {meetingBotStatus === 'joining' ? (
                <Wifi className="w-8 h-8 text-yellow-500 animate-pulse" />
              ) : (
                <Video className={`w-8 h-8 ${['in_meeting', 'in_call'].includes(meetingBotStatus) ? 'text-green-500' : 'text-zinc-400'}`} />
              )}
            </div>
            <h3 className="text-lg font-medium text-zinc-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
              {meetingBotStatus === 'joining' ? 'Bot Joining Meeting...' :
               ['in_meeting', 'in_call'].includes(meetingBotStatus) ? 'Bot in Meeting — Waiting for Speech' :
               ['error', 'fatal'].includes(meetingBotStatus) ? 'Bot Could Not Join' :
               'Meeting Bot'}
            </h3>
            <p className="text-zinc-500 text-sm max-w-sm">
              {meetingBotStatus === 'joining'
                ? 'The bot is connecting to your meeting. It may take 10–20 seconds to be admitted — please admit it from the waiting room if prompted.'
                : ['in_meeting', 'in_call'].includes(meetingBotStatus)
                ? 'The bot is listening. Transcripts and AI coaching will appear here within a few seconds of someone speaking.'
                : ['error', 'fatal'].includes(meetingBotStatus)
                ? 'The bot was unable to join. Check that the meeting link is correct and that the host has admitted the bot. End the call and try again with a fresh link.'
                : 'The bot will capture the conversation and transcribe it in real-time.'}
            </p>
          </>
        ) : (
          <>
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isListening ? 'bg-red-100' : 'bg-zinc-100'}`}>
              <Headphones className={`w-8 h-8 ${isListening ? 'text-red-500' : 'text-zinc-400'}`} />
            </div>
            <h3 className="text-lg font-medium text-zinc-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
              {isListening ? 'Listening...' : 'Ready to Listen'}
            </h3>
            <p className="text-zinc-500 text-sm max-w-xs">
              {isListening 
                ? 'Speak clearly into your microphone. The conversation will appear here in real-time.'
                : 'Start your call and click "Start Listening" to begin live transcription.'}
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <ScrollArea className="flex-1 p-6" ref={scrollRef} data-testid="transcript-container">
      <div className="space-y-4">
        {transcript.map((entry, index) => (
          <div
            key={entry.id || index}
            className={`flex gap-3 fade-in-up ${entry.speaker === 'rep' ? '' : ''}`}
            data-testid={`transcript-entry-${index}`}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
              entry.speaker === 'rep' 
                ? 'bg-brand-accent-muted text-brand-primary' 
                : 'bg-zinc-100 text-zinc-600'
            }`}>
              <User className="w-4 h-4" />
            </div>
            <div className={`flex-1 p-3 rounded-md ${
              entry.speaker === 'rep' 
                ? 'bg-zinc-100' 
                : 'bg-white border border-zinc-200'
            }`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                  {entry.speaker === 'rep' ? 'You' : entry.source === 'meeting_bot' ? entry.speaker : 'Prospect'}
                </span>
                {entry.timestamp && (
                  <span className="text-xs text-zinc-400">
                    {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
              <p className="text-zinc-900 text-sm leading-relaxed">{entry.text}</p>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
    </ScrollArea>
  );
}
